/**
 * Radar — several answers to the same set of questions, laid over each other.
 *
 * Two screens draw one: the results screen puts a polygon per rater in that
 * rater's own colour, and a game's page puts a polygon per night in one hue at
 * five strengths, newest strongest. Same component, because the shape of the
 * question is the same — one value per aspect, several sets of them at once.
 *
 * Three things about the geometry are worth knowing before changing any of it.
 *
 *  1. **The radius IS the stored rating.** Every rating in this app is an
 *     integer 1..10 counting half stars (`shared/stars.ts`), so a spoke needs no
 *     per-aspect normalisation and no rescaling of bipolar tracks: 10 is the top
 *     of every ladder in the app and 1 is the bottom of all of them. The lowest
 *     answer therefore sits a tenth of the way out rather than on the centre
 *     point, which is what keeps "Pure luck" from being drawn as a collapsed
 *     spike indistinguishable from no answer at all.
 *  2. **A missing value is bridged, never zeroed.** An aspect somebody skipped,
 *     called n/a, or that is still blind for the whole table has no vertex, and
 *     the outline runs straight from the last answered spoke to the next. A
 *     zero would be a claim — the bottom of the ladder — about somebody who said
 *     nothing. The chord visibly cuts inside the ring instead.
 *  3. **The drawing is `aria-hidden`.** It is pure geometry with no reading
 *     order, and both screens print the same answers in words underneath. The
 *     legend is not hidden: it is a real control, and it is how a keyboard picks
 *     one shape out of the stack.
 *
 * Area means nothing. Five of the seven seeded aspects are descriptive — the
 * outer rim of Luck ↔ Strategy is "Pure skill", which is not better than "Pure
 * luck" — and on Pacing the good answer is the middle of the track. Callers pass
 * the caption that says so; this component deliberately draws no total, no fill
 * ranking and no "biggest shape" affordance of any kind.
 */

import type { ReactNode } from "react";

import "./radar.css";

/** Below this a polygon is not a shape, and both callers say so in a line of copy. */
export const RADAR_MIN_AXES = 3;

/** Top of every ladder in the app: five stars, stored as ten halves. */
export const RADAR_MAX = 10;

export interface RadarAxis {
  id: string;
  /** The aspect's name. Stop words live in the blocks below, not on the rim. */
  name: string;
}

export interface RadarSeries {
  id: string;
  label: string;
  /** Any CSS colour. Applied through `color`, so `var(--pb-slate)` works too. */
  colour: string;
  /**
   * Star value 1..10 per axis id. An absent key and an explicit null are the
   * same statement — no answer — and both bridge.
   */
  points: Readonly<Record<string, number | null>>;
  /**
   * 0..1 presence. The night ramp carries recency in it; seat colours leave it
   * at 1, since a player is not more or less of a player.
   */
  strength?: number;
}

// The drawing box. Wider than tall on purpose: the circle is sized by the
// height, and the slack either side is what the east and west axis labels are
// written into. Every number below is in these units, so the whole chart scales
// with the container and nothing has to be recomputed for a phone.
const VIEW_W = 320;
const VIEW_H = 240;
const CX = VIEW_W / 2;
const CY = 118;
const R = 74;
/** Where the axis names sit, measured from the centre. */
const LABEL_R = R + 12;
const RINGS = [2, 4, 6, 8, 10];

export interface Point {
  x: number;
  y: number;
}

/**
 * Where one answer lands. Exported for the tests, which check the invariant that
 * matters rather than the pixels: the top of the scale reaches the rim, the
 * bottom stays off the centre, and the first axis points up.
 */
/**
 * Where spoke `index` points. The quarter turn is what puts the first one
 * straight up, and every mark on an axis — vertex, label, anchor — has to read
 * it from here or the labels drift off the spokes they name.
 */
function angleFor(index: number, count: number): number {
  return (index / count) * Math.PI * 2 - Math.PI / 2;
}

export function radarPoint(index: number, count: number, value: number): Point {
  const angle = angleFor(index, count);
  const r = (Math.max(0, Math.min(value, RADAR_MAX)) / RADAR_MAX) * R;
  return { x: CX + Math.cos(angle) * r, y: CY + Math.sin(angle) * r };
}

function labelPoint(index: number, count: number): Point {
  const angle = angleFor(index, count);
  return { x: CX + Math.cos(angle) * LABEL_R, y: CY + Math.sin(angle) * LABEL_R };
}

/** Which side of the tip the name is written on, so nothing overlaps the ring. */
function anchorFor(index: number, count: number): "start" | "middle" | "end" {
  const x = Math.cos(angleFor(index, count));
  if (x > 0.2) return "start";
  if (x < -0.2) return "end";
  return "middle";
}

/** One polyline through the points, rounded the same way for rings and shapes. */
function pathFrom(points: readonly Point[]): string {
  return points
    .map((p, i) => `${i === 0 ? "M" : "L"}${p.x.toFixed(2)} ${p.y.toFixed(2)}`)
    .join(" ");
}

function ringPath(count: number, value: number): string {
  const points = Array.from({ length: count }, (_, i) => radarPoint(i, count, value));
  return `${pathFrom(points)} Z`;
}

/**
 * The outline for one series, with the gaps already bridged.
 *
 * Closed only from three answered spokes up: two make a line and one makes a
 * dot, and closing either would draw a shape the data does not have.
 */
export function seriesPath(axes: readonly RadarAxis[], series: RadarSeries): string {
  const answered: Point[] = [];
  axes.forEach((axis, i) => {
    const value = series.points[axis.id];
    if (typeof value !== "number") return;
    answered.push(radarPoint(i, axes.length, value));
  });
  if (answered.length < 2) return "";
  const path = pathFrom(answered);
  return answered.length >= 3 ? `${path} Z` : path;
}

export interface RadarProps {
  axes: readonly RadarAxis[];
  series: readonly RadarSeries[];
  /** The series drawn at full strength while the rest are dimmed. */
  focusId?: string | null;
  /** Omit to render the legend as plain text rather than as buttons. */
  onFocus?: (id: string | null) => void;
  /** Sits under the drawing. Both callers use it to say area is not a score. */
  caption?: ReactNode;
}

export function Radar(props: RadarProps): ReactNode {
  const { axes, series, focusId = null, onFocus, caption } = props;
  if (axes.length < RADAR_MIN_AXES) return null;

  const count = axes.length;
  const dimmed = focusId !== null;

  return (
    <div className="pb-radar">
      {/* Hidden from assistive tech on purpose — see the header note. */}
      <svg
        className="pb-radar__plot"
        viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
        role="presentation"
        aria-hidden="true"
        focusable="false"
      >
        <g className="pb-radar__grid">
          {RINGS.map((value) => (
            <path key={`ring-${value}`} d={ringPath(count, value)} />
          ))}
          {axes.map((axis, i) => {
            const tip = radarPoint(i, count, RADAR_MAX);
            return (
              <line
                key={`spoke-${axis.id}`}
                x1={CX}
                y1={CY}
                x2={tip.x.toFixed(2)}
                y2={tip.y.toFixed(2)}
              />
            );
          })}
        </g>

        <g className="pb-radar__labels">
          {axes.map((axis, i) => {
            const at = labelPoint(i, count);
            return (
              <text
                key={`label-${axis.id}`}
                x={at.x.toFixed(2)}
                y={at.y.toFixed(2)}
                textAnchor={anchorFor(i, count)}
                dominantBaseline="middle"
              >
                {axis.name}
              </text>
            );
          })}
        </g>

        {/* Painted back to front, so the caller's FIRST series ends up on top of
            the stack while its legend below still reads in the caller's order.
            The game page lists nights newest first and means it: the newest
            night is the one being compared against, and it must not end up
            underneath four older shapes. */}
        {[...series].reverse().map((s) => {
          const path = seriesPath(axes, s);
          const strength = s.strength ?? 1;
          const focused = focusId === s.id;
          return (
            <g
              key={s.id}
              className="pb-radar__series"
              data-state={focused ? "focus" : dimmed ? "dim" : "rest"}
              style={{ color: s.colour, opacity: strength }}
            >
              {path && <path className="pb-radar__shape" d={path} />}
              {axes.map((axis, i) => {
                const value = s.points[axis.id];
                if (typeof value !== "number") return null;
                const at = radarPoint(i, count, value);
                return (
                  <circle
                    key={`${s.id}-${axis.id}`}
                    className="pb-radar__vertex"
                    cx={at.x.toFixed(2)}
                    cy={at.y.toFixed(2)}
                    r={2.8}
                  />
                );
              })}
            </g>
          );
        })}
      </svg>

      {series.length > 0 && (
        <ul className="pb-radar__legend">
          {series.map((s) => {
            const swatch = (
              <span
                className="pb-radar__swatch"
                style={{ background: s.colour, opacity: s.strength ?? 1 }}
              />
            );
            if (!onFocus) {
              return (
                <li key={s.id} className="pb-radar__key">
                  {swatch}
                  <span>{s.label}</span>
                </li>
              );
            }
            const focused = focusId === s.id;
            return (
              <li key={s.id}>
                <button
                  type="button"
                  className="pb-radar__key"
                  // A toggle rather than a radio: pressing the focused one puts
                  // everybody back, so there is always a way out of an isolated
                  // shape without hunting for a "show all" control.
                  aria-pressed={focused}
                  onClick={() => onFocus(focused ? null : s.id)}
                >
                  {swatch}
                  <span>{s.label}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {caption && <p className="pb-hint pb-radar__caption">{caption}</p>}
    </div>
  );
}
